import type { AppBarItemDef } from '@batiflow/ui';
import {
  Building24Filled,
  Building24Regular,
  Delete24Filled,
  Delete24Regular,
  DocumentSearch24Filled,
  DocumentSearch24Regular,
  Money24Filled,
  Money24Regular,
  ShieldKeyhole24Filled,
  ShieldKeyhole24Regular,
  ShieldLock24Filled,
  ShieldLock24Regular,
  Warning24Filled,
  Warning24Regular,
} from '@fluentui/react-icons';
import type { TFunction } from 'i18next';

/** The pages of the back-office, in navigation order. `path` is the route's address. */
export const NAV_PAGES = [
  { id: 'organizations', path: '/organizations' },
  { id: 'break-glass', path: '/break-glass' },
  { id: 'deletions', path: '/deletions' },
  { id: 'jobs', path: '/jobs' },
  { id: 'security', path: '/security' },
  { id: 'audit', path: '/audit' },
  { id: 'plans', path: '/plans' },
] as const;

export type NavPageId = (typeof NAV_PAGES)[number]['id'];

const ICONS: Record<NavPageId, Pick<AppBarItemDef, 'icon' | 'activeIcon'>> = {
  organizations: { icon: Building24Regular, activeIcon: Building24Filled },
  'break-glass': { icon: ShieldKeyhole24Regular, activeIcon: ShieldKeyhole24Filled },
  deletions: { icon: Delete24Regular, activeIcon: Delete24Filled },
  jobs: { icon: Warning24Regular, activeIcon: Warning24Filled },
  security: { icon: ShieldLock24Regular, activeIcon: ShieldLock24Filled },
  audit: { icon: DocumentSearch24Regular, activeIcon: DocumentSearch24Filled },
  plans: { icon: Money24Regular, activeIcon: Money24Filled },
};

/** Left navigation entries (Ctrl+Alt+1… shortcuts of the shell follow this order). */
export function navItems(t: TFunction<'backoffice'>): AppBarItemDef[] {
  return NAV_PAGES.map((page, index) => ({
    id: page.id,
    label: t(`nav.${page.id}`),
    href: page.path,
    shortcut: index + 1,
    // The phone bar holds six apps: deletions (rare, two admins) open from the Organisations page.
    onPhone: page.id !== 'deletions',
    ...ICONS[page.id],
  }));
}

/**
 * The page an address path belongs to (the organisation detail, and the account pages reached from
 * its members, belong to Organisations).
 */
export function pageOfPath(pathname: string): NavPageId | undefined {
  if (pathname.startsWith('/users/')) return 'organizations';
  return NAV_PAGES.find((page) => pathname === page.path || pathname.startsWith(`${page.path}/`))
    ?.id;
}
